import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { LightboxType } from '../../../../../../redux/Lightbox/options';
import { FileListModule } from '../../../../enum/FileListModule';
import LightboxActions from "bukku/redux/Lightbox/actions";
import { useEffect } from 'react';
import { usePrevious } from '../../../../../../helpers/useReadStateSuccess';
import _ from 'underscore';

const {
    open: openLightbox,
    setFiles: setLightboxFiles,
} = LightboxActions;

/**
 * @typedef {Object} File
 * @property {number} file_id - The ID of the file.
 * @property {string|undefined} created_at - The creation date of the file.
 * @property {Object} file - The file details.
 * @property {number} file.id - The ID of the file details.
 * @property {string} file.filename - The name of the file.
 * @property {string} file.mime_type - The MIME type of the file.
 * @property {number} file.size - The size of the file.
 * @property {string|undefined} file.url - The URL of the file (eg: "https://bukku-my-dev2.s3.ap-southeast-1.amazonaws.com/1/tutk8ryALEX5rARZ8GExiF2C7G4r83dyEePispl1.png?X-Amz-Content-Sha256=UNSIGNED-PAYLOAD&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAUGZSJ6CUQG5E3OFE%2F20240327%2Fap-southeast-1%2Fs3%2Faws4_request&X-Amz-Date=20240327T100901Z&X-Amz-SignedHeaders=host&X-Amz-Expires=600&X-Amz-Signature=8dbf45e22835d6b44033731f45bd04d67f265afa9d3f745d1eeab7fd7f3d1842")
 * @property {null|undefined}   file.data - The data of the file.
 * @property {string|undefined} file.created_at - The creation date of the file details.
 * @property {string|undefined} file.updated_at - The update date of the file details.
 * @property {number|undefined} id - The ID.
 * @property {boolean|undefined} is_shared - The shared status of the file.
 */

/**
 * Hook to handle lightbox operations.
 * @param {File[]} files
 * @param {FileListModule} moduleType
 * @returns {{onFileItemClick: (listItem:File|null) => {}}} The file item click handler.
 *  - listItem: []       when in panelOther
 *  - listItem: null     when in FilesPreview (transaction list / contact list / file manager card)
 */
export const useLightboxHandler = (files, moduleType) => {
    const dispatch = useDispatch();
    const lightboxFiles = useSelector(state => state.Lightbox.files, shallowEqual);

    // When in PanelOther:
    // refresh lightbox files when files change (readSucess / add new file / delete file)
    const prevFiles = usePrevious(files);
    useEffect(() => {
        if (!_.isEqual(prevFiles, files))
            dispatch(setLightboxFiles(files));
    }, [files, prevFiles, dispatch]);

    const onFileItemClick = listItem => {
        let lighboxType;
        switch (moduleType) {
            case FileListModule.TRANSACTION:
                lighboxType = LightboxType.TRANSACTION;
                break;
            case FileListModule.CONTACT:
                lighboxType = LightboxType.CONTACT;
                break;
            case FileListModule.FILE_MANAGER:
                lighboxType = LightboxType.file_manager;
                break;
            default:
                throw new Error('Invalid moduleType', moduleType);
        }

        // When in FilesPreview (transaction list / contact list / file manager card)
        if (!listItem) {
            dispatch(openLightbox(0, files, lighboxType)); // always open first file
        }

        // When in panelOther
        else {
            const index = lightboxFiles.findIndex(f => f.file_id === listItem.file_id);
            dispatch(openLightbox(index, lightboxFiles, lighboxType));
        }
    };

    return {
        onFileItemClick,
    };
};
